import { create } from 'zustand';
import { getTimeClockPolicy } from '@/api/generated/time-clock-policy/time-clock-policy';
import type { FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockPolicyDto } from '@/api/generated/models';
import { showApiError } from '@/errors';
import { i18n } from '@/i18n';
import { useToastStore } from '@/store/useToastStore';

export type TimeClockPolicy = FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockPolicyDto;

const api = getTimeClockPolicy();

interface TimeClockPolicyState {
  policy: TimeClockPolicy | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** Loads the policy once per session; `force` reloads it (e.g. on the settings page). */
  fetchPolicy: (options?: { force?: boolean }) => Promise<void>;
  /** Saves the policy; returns the stored version, or null when the server rejected it. */
  updatePolicy: (policy: TimeClockPolicy) => Promise<TimeClockPolicy | null>;
}

// why: several components (header widget, dashboard, time clock page) ask for the policy on
// mount — sharing the in-flight request keeps it to one GET per session.
let inFlight: Promise<void> | null = null;

export const useTimeClockPolicyStore = create<TimeClockPolicyState>((set, get) => ({
  policy: null,
  loading: false,
  saving: false,
  error: null,

  fetchPolicy: async ({ force = false } = {}) => {
    if (!force && get().policy) return;
    if (inFlight) return inFlight;
    set({ loading: true, error: null });
    inFlight = (async () => {
      try {
        const policy = await api.getApiV1TimeclockPolicy();
        set({ policy, loading: false });
      } catch (err) {
        console.error('[TimeClockPolicyStore] fetch failed', err);
        set({ error: 'tenantSettings:timeClockPolicy.fetchFailed', loading: false });
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  },

  updatePolicy: async (policy) => {
    set({ saving: true });
    try {
      const updated = await api.putApiV1TimeclockPolicy(policy);
      set({ policy: updated, saving: false });
      useToastStore
        .getState()
        .addToast(i18n.t('tenantSettings:timeClockPolicy.saved'), 'success');
      return updated;
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      return null;
    }
  },
}));
